import { useEffect, useRef } from "react";

export function LiveWaveform({ active, level }: { active: boolean; level: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const historyRef = useRef<number[]>(Array.from({ length: 44 }, () => 0));

  useEffect(() => {
    const history = historyRef.current;
    history.push(active ? level : 0);
    while (history.length > 44) history.shift();
    drawWaveform(canvasRef.current, history, active);
  }, [active, level]);

  return (
    <canvas
      ref={canvasRef}
      className="live-waveform"
      data-testid="live-waveform"
      data-active={active ? "true" : "false"}
      data-level={active ? level : 0}
      width={440}
      height={88}
      aria-label="Live input waveform"
    />
  );
}

function drawWaveform(canvas: HTMLCanvasElement | null, history: number[], active: boolean): void {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#1a1620";
  ctx.fillRect(0, 0, width, height);

  const barWidth = width / history.length;
  for (let i = 0; i < history.length; i += 1) {
    const value = Math.max(0.04, Math.min(1, history[i]));
    const barHeight = active ? Math.max(5, value * (height - 18)) : 5;
    const x = i * barWidth + 2;
    const y = (height - barHeight) / 2;
    const hue = active ? i / history.length : 0;
    ctx.fillStyle = active
      ? blendColor("#5fd6a0", "#b39dff", hue)
      : "#4a3f5a";
    roundRect(ctx, x, y, Math.max(2, barWidth - 4), barHeight, 4);
    ctx.fill();
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function blendColor(from: string, to: string, amount: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * amount);
  return `rgb(${mix(a.r, b.r)}, ${mix(a.g, b.g)}, ${mix(a.b, b.b)})`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const value = Number.parseInt(hex.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}
